import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ActionContext } from './actions/actions';
import { pasteImageFile } from './actions/clipboard';
import { AuthGate } from './auth/AuthGate';
import { useAuth } from './auth/AuthContext';
import { Dashboard } from './dashboard/Dashboard';
import { BoardCanvas } from './canvas/BoardCanvas';
import { CommentsLayer, createCommentPin } from './canvas/CommentsLayer';
import { EmbedLayer } from './canvas/EmbedLayer';
import { VoteBadgesLayer } from './canvas/VoteBadgesLayer';
import { TextEditorOverlay } from './canvas/TextEditorOverlay';
import { CallProvider } from './call/CallProvider';
import { CallChatWindow } from './call/CallChatWindow';
import { CallWindow } from './call/CallWindow';
import { IncomingCallBanner } from './call/IncomingCallBanner';
import { ScreenShareWindow } from './call/ScreenShareWindow';
import { useAutoFollowPresenter } from './call/useAutoFollowPresenter';
import { BoardDoc, type BoardRole } from './collab/BoardDoc';
import { useIsEmpty } from './collab/hooks';
import { clearImageCache } from './render/imageCache';
import { renderThumbnail } from './export/exportBoard';
import { useFollowPeer } from './hooks/useFollowPeer';
import { useShortcuts } from './hooks/useShortcuts';
import { ApiError, api } from './lib/api';
import { identityFromAccount, loadIdentity } from './lib/identity';
import { animations } from './render/animations';
import { waitForFonts } from './render/measure';
import { invalidate, scratch } from './state/scratch';
import { useEditor } from './state/store';
import { ContextMenu } from './ui/ContextMenu';
import { FollowBanner } from './ui/FollowBanner';
import { OffscreenPeerHints } from './ui/OffscreenPeerHints';
import { Minimap } from './ui/Minimap';
import { PresentationBar } from './ui/PresentationBar';
import { StylePanel } from './ui/StylePanel';
import { Toolbar } from './ui/Toolbar';
import { NotebookWorkspace } from './canvas/NotebookWorkspace';
import { BoardNotice } from './ui/BoardNotice';
import { TopBar } from './ui/TopBar';
import { ZoomControls } from './ui/ZoomControls';

const BOARD_PARAM = 'b';

/** Board id currently in the URL, or null when the dashboard should show. */
function boardIdFromLocation(): string | null {
  return new URLSearchParams(window.location.search).get(BOARD_PARAM);
}

/**
 * Two screens, routed off one query parameter.
 *
 * No router library: there are exactly two destinations, and `?b=<id>` stays
 * the board's address — though not, any more, a way in on its own: both
 * branches sit behind the auth gate, and the board itself asks the server
 * whether this account may open it.
 */
export function App() {
  const { user: authUser } = useAuth();
  const guestIdentity = useMemo(() => loadIdentity(), []);
  // A signed-in account's name and colour take over presence everywhere —
  // dashboard and board alike — while a guest keeps the anonymous one.
  const identity = useMemo(
    () => (authUser ? identityFromAccount(authUser) : guestIdentity),
    [authUser, guestIdentity],
  );
  const [boardId, setBoardId] = useState<string | null>(boardIdFromLocation);

  useEffect(() => {
    const onPopState = () => setBoardId(boardIdFromLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openBoard = useCallback((id: string) => {
    window.history.pushState(null, '', `?${BOARD_PARAM}=${encodeURIComponent(id)}`);
    setBoardId(id);
  }, []);

  const exitBoard = useCallback(() => {
    window.history.pushState(null, '', window.location.pathname);
    setBoardId(null);
  }, []);

  return (
    <AuthGate>
      {boardId ? (
        <BoardRoute boardId={boardId} identity={identity} onExit={exitBoard} />
      ) : (
        <Dashboard onOpenBoard={openBoard} />
      )}
    </AuthGate>
  );
}

/** Outcome of asking the server whether this account may open the board. */
type BoardAccess =
  | { state: 'loading' }
  | {
      state: 'ok';
      role: BoardRole;
      name: string;
      orgId: string;
      canManageMembers: boolean;
    }
  | { state: 'denied' }
  | { state: 'missing' }
  | { state: 'error'; message: string };

function BoardRoute({
  boardId,
  identity,
  onExit,
}: {
  boardId: string;
  identity: ReturnType<typeof loadIdentity>;
  onExit: () => void;
}) {
  const [doc, setDoc] = useState<BoardDoc | null>(null);
  const [access, setAccess] = useState<BoardAccess>({ state: 'loading' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Access is checked before the socket is opened. The server closes an
  // unauthorised socket anyway, but a closed socket looks like a network
  // problem; a straight answer from REST is what turns it into a screen that
  // explains itself.
  useEffect(() => {
    let cancelled = false;
    setAccess({ state: 'loading' });
    (async () => {
      try {
        const board = await api.getBoard(boardId);
        if (cancelled) return;
        if (!board.myRole) setAccess({ state: 'denied' });
        else
          setAccess({
            state: 'ok',
            role: board.myRole,
            name: board.name,
            orgId: board.orgId,
            canManageMembers: board.canManageMembers,
          });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setAccess({ state: 'denied' });
        else if (e instanceof ApiError && e.status === 404) setAccess({ state: 'missing' });
        else setAccess({ state: 'error', message: e instanceof Error ? e.message : 'Ошибка' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  const role = access.state === 'ok' ? access.role : null;

  useEffect(() => {
    if (!role) return;
    const instance = new BoardDoc({ boardId, role, identity });
    setDoc(instance);
    // Web fonts change text metrics, so the measurement cache is dropped and
    // the board repainted once they are ready.
    void waitForFonts().then(invalidate);
    return () => {
      instance.destroy();
      clearImageCache();
      setDoc(null);
    };
  }, [boardId, role, identity]);

  if (access.state === 'denied' || access.state === 'missing') {
    return <NoBoardAccess kind={access.state} onExit={onExit} />;
  }
  if (access.state === 'error') {
    return <NoBoardAccess kind="error" message={access.message} onExit={onExit} />;
  }

  // Keyed by board id so switching boards rebuilds the whole editor rather
  // than trying to re-point a live renderer at a different document.
  if (access.state !== 'ok' || !doc) return <div className="app-loading">Открытие доски…</div>;
  return (
    <BoardShell
      key={boardId}
      doc={doc}
      identity={identity}
      fileInputRef={fileInputRef}
      onExit={onExit}
      orgId={access.orgId}
      boardName={access.name}
    />
  );
}

function BoardShell({
  doc,
  identity,
  fileInputRef,
  onExit,
  orgId,
  boardName,
}: {
  doc: BoardDoc;
  identity: ReturnType<typeof loadIdentity>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onExit: () => void;
  orgId: string;
  boardName: string;
}) {
  const ctx: ActionContext = useMemo(() => ({ doc, identity }), [doc, identity]);
  const isEmpty = useIsEmpty(doc);
  const canEdit = doc.canEdit();
  const canComment = doc.canComment();

  // Refreshes the dashboard card's preview image. Best-effort and silent —
  // a stale or missing thumbnail is a cosmetic issue, never worth surfacing
  // an error for, and only an editor's snapshot is worth persisting.
  const captureThumbnail = useCallback(async () => {
    if (!canEdit) return;
    try {
      const dataUrl = await renderThumbnail(doc);
      if (dataUrl) await api.updateBoard(doc.boardId, { thumbnail: dataUrl });
    } catch {
      // Ignored — see comment above.
    }
  }, [doc, canEdit]);

  useEffect(() => {
    // Covers the tab being closed outright, not just the in-app "back" button.
    const onHide = () => {
      if (document.visibilityState === 'hidden') void captureThumbnail();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [captureThumbnail]);

  const handleExit = useCallback(() => {
    void captureThumbnail();
    onExit();
  }, [captureThumbnail, onExit]);

  // Stable identities on purpose. These are handed to effects that own the
  // canvas pointer and keyboard listeners, and a fresh function per render
  // would make those listeners detach and re-attach constantly — including in
  // the middle of a drag.
  const openFilePicker = useCallback(() => fileInputRef.current?.click(), [fileInputRef]);
  const shortcutOptions = useMemo(() => ({ onUploadImage: openFilePicker }), [openFilePicker]);
  const onCreateComment = useCallback(
    (at: Parameters<typeof createCommentPin>[2], threadId: string) =>
      createCommentPin(doc, identity, at, threadId),
    [doc, identity],
  );

  const presenting = useEditor((s) => s.presentingFrameId !== null);
  const inNotebook = useEditor((s) => s.enteredNotebookId !== null);

  useShortcuts(ctx, shortcutOptions);
  useFollowPeer(doc);

  // Pull the dashboard title into the live document once the local cache
  // (and the first server sync) have landed — otherwise an empty IndexedDB
  // copy would overwrite the seeded name a moment later.
  useEffect(() => {
    let cancelled = false;
    const apply = () => {
      if (!cancelled) doc.syncNameFromServer(boardName);
    };
    void doc.offlineReady.then(apply);
    const provider = doc.provider;
    if (provider) {
      const onSync = (synced: boolean) => {
        if (synced) apply();
      };
      provider.on('sync', onSync);
      if (provider.synced) apply();
      return () => {
        cancelled = true;
        provider.off('sync', onSync);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [doc, boardName]);

  // Broadcast frame-deck presenting so peers on a call with this person can
  // auto-follow it (`useAutoFollowPresenter`) without a second UI for it.
  useEffect(() => {
    doc.setPresence({ presenting });
  }, [doc, presenting]);

  // Development-only inspection handle. Board bugs are almost always "what is
  // in the document / what is selected", and a console handle answers that far
  // faster than instrumenting the render path.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as Record<string, unknown>).kanvas = {
      doc,
      state: () => useEditor.getState(),
      nodes: () => doc.all(),
      scratch,
      animations,
    };
  }, [doc]);

  return (
    <CallProvider doc={doc} identity={identity}>
      <CallBoardEffects doc={doc} />
      <div className={`app ${presenting ? 'is-presenting' : ''} ${inNotebook ? 'is-notebook' : ''}`}>
        {!presenting && !inNotebook && (
          <TopBar
            ctx={ctx}
            onExit={handleExit}
            orgId={orgId}
            boardName={boardName}
          />
        )}

        <main className="board-area">
          {!inNotebook && (
            <>
              <BoardCanvas doc={doc} identity={identity} onCreateComment={onCreateComment} />
              <EmbedLayer doc={doc} />
              {canEdit && <TextEditorOverlay doc={doc} />}
              <CommentsLayer doc={doc} identity={identity} />
              <VoteBadgesLayer doc={doc} identity={identity} />

              {!presenting && canComment && (
                <Toolbar onUploadImage={openFilePicker} commentOnly={!canEdit} />
              )}
              {!presenting && canEdit && <StylePanel ctx={ctx} />}
              {!presenting && (
                <>
                  <ZoomControls ctx={ctx} />
                  <Minimap doc={doc} />
                </>
              )}
              <FollowBanner doc={doc} />
              <OffscreenPeerHints doc={doc} />
              <PresentationBar doc={doc} />
              {!presenting && <IncomingCallBanner />}

              {isEmpty && !presenting && <EmptyState />}
            </>
          )}
          {inNotebook && <NotebookWorkspace doc={doc} canEdit={canEdit} />}
          <BoardNotice />
        </main>

        {/* Both float above the whole app rather than inside the board area:
            they are windows the user places, not board furniture. */}
        <CallWindow boardId={doc.boardId} />
        <CallChatWindow boardId={doc.boardId} />
        <ScreenShareWindow boardId={doc.boardId} />

        {!presenting && !inNotebook && canEdit && <ContextMenu ctx={ctx} />}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={async (e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            const vp = useEditor.getState().viewport;
            let offset = 0;
            for (const file of files) {
              await pasteImageFile(
                ctx,
                file,
                {
                  x: vp.x + vp.width / 2 / vp.zoom + offset,
                  y: vp.y + vp.height / 2 / vp.zoom + offset,
                },
              );
              offset += 32;
            }
          }}
        />
      </div>
    </CallProvider>
  );
}

/** Call-aware board effects must render below CallProvider to access its context. */
function CallBoardEffects({ doc }: { doc: BoardDoc }) {
  useAutoFollowPresenter(doc);
  return null;
}

/**
 * What a `?b=` link now leads to when the account behind it has no business
 * on that board — a link is no longer permission in itself.
 */
function NoBoardAccess({
  kind,
  message,
  onExit,
}: {
  kind: 'denied' | 'missing' | 'error';
  message?: string;
  onExit: () => void;
}) {
  const title =
    kind === 'missing'
      ? 'Доска не найдена'
      : kind === 'error'
        ? 'Не удалось открыть доску'
        : 'Нет доступа к этой доске';
  const body =
    kind === 'missing'
      ? 'Возможно, её удалили, или ссылка была скопирована с ошибкой.'
      : kind === 'error'
        ? (message ?? 'Попробуйте обновить страницу.')
        : 'Открыть её может только владелец доски и те, кого он пригласил. Попросите приглашение у владельца.';

  return (
    <div className="board-denied">
      <div className="board-denied-card">
        <h1>{title}</h1>
        <p>{body}</p>
        <button type="button" className="primary-btn" onClick={onExit}>
          К списку досок
        </button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="empty-state">
      <h1>Начните творить</h1>
      <ul>
        <li>
          <kbd>N</kbd> заметка · <kbd>R</kbd> фигура · <kbd>T</kbd> текст · <kbd>P</kbd> рисование
        </li>
        <li>
          Дважды щёлкните в любом месте, чтобы добавить заметку
        </li>
        <li>
          <kbd>Space</kbd> перетаскивание · <kbd>Ctrl</kbd> прокрутка для масштабирования
        </li>
        <li>Вставьте изображение, ссылку или список строк прямо на доску</li>
      </ul>
    </div>
  );
}
